import React, { useCallback, useContext } from "react";

import { ApiRequest } from "api/makeApiRequest/ApiRequest";
import { Auth } from "api/makeApiRequest";
import { AuthContext } from "components/AuthProvider/AuthProvider";
import { Params as DataSaverParams } from "api/makeApiRequestType/makeSubmitEditableTableChanges";

type Params<RowData> = {
	setWaitModalIsShowing: React.Dispatch<React.SetStateAction<boolean>>;
	setWaitModalTitle: React.Dispatch<React.SetStateAction<string>>;
	dataSaver: (data: DataSaverParams<RowData>, auth: Auth) => ApiRequest<{}>;
	onSaved?: (timestamp: number) => void;
};

export function useOnEditEditableTable<RowData>({
	setWaitModalIsShowing,
	setWaitModalTitle,
	dataSaver,
	onSaved
}: Params<RowData>) {
	const [{ uid }] = useContext(AuthContext);

	const onEdit = useCallback(
		(rows: { old?: RowData; new?: RowData }[]) => {
			const title = rows.every(row => !row.new) ? "Deleting" : "Saving";

			setWaitModalTitle(title);
			setWaitModalIsShowing(true);

			if (!uid) {
				throw new Error("No UID");
			}

			const req = dataSaver(
				{
					uid,
					rows
				},
				{ uid }
			);

			const ready = req.ready.then(response => {
				if (onSaved) {
					onSaved(new Date().getTime());
				}
				setWaitModalIsShowing(false);
			});

			return {
				ready,
				abort: () => req.abort(),
				aborted: () => req.aborted()
			};
		},
		[uid, setWaitModalTitle, setWaitModalIsShowing, dataSaver, onSaved]
	);

	return onEdit;
}
